import { createTheme } from "@mui/material/styles";
import { tokens as t, tokenValues, cssVariables } from "./utils/figmaTokens";

const FONT = '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif';

const text = (fontSize, lineHeight, fontWeight, letterSpacing = 0, extra = {}) => ({
  fontFamily: FONT,
  fontSize,
  lineHeight: `${lineHeight}px`,
  fontWeight,
  letterSpacing,
  ...extra,
});

// Figma's text styles as new variants: <Typography variant="titleSection">. MUI's own
// variants (h1–h6, body1, body2, caption…) keep MUI's sizes, so a page not redrawn yet keeps
// its headings.
const figmaText = {
  titleMedium: text(25, 32, 600, "-0.3px"),
  titleSection: text(18, 24, 600, "-0.2px"),
  titleCard: text(16, 24, 600, "-0.1px"),
  titleMetric: text(21, 24, 600, "-0.4px"),
  bodyMedium: text(16, 26, 400),
  bodyMediumStrong: text(16, 26, 500),
  bodySmall: text(14, 22, 400),
  bodySmallStrong: text(14, 22, 500),
  labelMedium: text(12, 16, 500, "0.2px"),
  labelCaption: text(12, 16, 500, "0.6px", { textTransform: "uppercase" }),
  // The stat strip's label and the child mini-card's line (10697:16585): a raw 11/14 style
  // that no named Figma style matches.
  labelMicro: text(11, 14, 500, "0.55px"),
};

// A soft chip (Chip on 10641:4377; the ticket chips on 10810:61045): the tone's fill and ink.
const softChip = (color, bg, ink) => ({
  props: { variant: "filled", color },
  style: {
    backgroundColor: bg,
    color: ink,
    "&.MuiChip-clickable:hover": { backgroundColor: bg, filter: "brightness(0.96)" },
    // MUI's own focus style paints the tone's dark main behind the dark ink.
    "&.Mui-focusVisible": { backgroundColor: bg, outline: `2px solid ${t.brand.border}`, outlineOffset: "1px" },
  },
});

// MUI's palette for one mode. MUI works out hover and contrast colours from these, so they are
// the colours themselves, not CSS variables.
const palette = (v) => ({
  primary: { main: v.brand.primary },
  secondary: { main: v.legacySecondary },
  success: { main: v.success.main },
  warning: { main: v.warning.main },
  error: { main: v.error.main },
  info: { main: v.info.main },
  text: { primary: v.text.primary, secondary: v.text.secondary, disabled: v.text.disabled },
  divider: v.divider,
  background: { default: v.surface.page, paper: v.surface.default },
});

// Light and dark (047 D105–D111). MUI writes `data-mui-color-scheme` on <html>, keeps the choice in
// local storage ("mui-mode"), and its components read the matching palette. The page's own
// colours, `tokens`, are CSS variables declared below for both modes.
const theme = createTheme({
  cssVariables: { colorSchemeSelector: "data-mui-color-scheme" },
  colorSchemes: {
    light: { palette: palette(tokenValues.light) },
    // MUI's own blue (text buttons, links, its icons, a focused field, checkboxes, switches,
    // spinners) is Figma's dark link blue: #0b59cb is too dim on a dark card (047 P134).
    dark: { palette: { ...palette(tokenValues.dark), primary: { main: tokenValues.dark.text.link } } },
  },
  // shape.borderRadius stays MUI's 4: old pages write `borderRadius: 2` in sx, which MUI
  // multiplies by it. Every radius below is a px string.
  typography: { fontFamily: FONT, ...figmaText },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": cssVariables(tokenValues.light),
        '[data-mui-color-scheme="dark"]': cssVariables(tokenValues.dark),
      },
    },
    // MUI lightens a raised surface in dark with a white overlay; Figma's dark panels, menus and
    // dialogs are the card colour (047 P124).
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          titleMedium: "h1",
          titleSection: "h2",
          titleCard: "h3",
          titleMetric: "div",
          bodyMedium: "p",
          bodyMediumStrong: "p",
          bodySmall: "p",
          bodySmallStrong: "p",
          labelMedium: "span",
          labelCaption: "span",
          labelMicro: "span",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          // Every page: sentence-case buttons (047 D7).
          textTransform: "none",
          variants: [
            // Page and card actions (Button 10704:8817): 44 px tall, 12 px radius. A page asks
            // for it with size="large"; other buttons keep MUI's box until their page's step.
            {
              props: { size: "large" },
              style: {
                padding: "12px",
                minWidth: 44,
                borderRadius: "12px",
                fontSize: 14,
                lineHeight: "20px",
                fontWeight: 500,
                letterSpacing: "0.25px",
                "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "8px" },
                "& .MuiButton-endIcon": { marginLeft: "8px", marginRight: 0 },
              },
            },
            {
              props: { size: "large", variant: "outlined" },
              style: {
                padding: "10.5px",
                border: `1.5px solid ${t.buttonBorder}`,
                color: t.text.primary,
                "&:hover": { border: `1.5px solid ${t.buttonBorder}`, backgroundColor: t.hover },
              },
            },
            // A filled button keeps Figma's #0b59cb with white words in dark, where MUI's own blue
            // is the light link blue (047 P134).
            {
              props: { variant: "contained", color: "primary" },
              style: ({ theme: th }) =>
                th.applyStyles("dark", {
                  "--variant-containedBg": t.brand.primary,
                  "--variant-containedColor": t.brand.on,
                  // MUI's own hover guard: on a phone a tapped button does not stay lit.
                  "@media (hover: hover)": { "&:hover": { "--variant-containedBg": t.brand.hover } },
                }),
            },
          ],
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 26,
          borderRadius: "9999px",
          fontSize: 12,
          lineHeight: "16px",
          fontWeight: 500,
          "& .MuiChip-label": { paddingLeft: "10px", paddingRight: "10px" },
          variants: [
            { props: { size: "small" }, style: { height: 26 } },
            softChip("default", t.neutral.bg, t.text.primary),
            softChip("primary", t.brand.subtle, t.brand.text),
            softChip("success", t.success.bg, t.success.ink),
            softChip("warning", t.warning.bg, t.warning.ink),
            softChip("error", t.error.bg, t.error.ink),
            softChip("info", t.info.bg, t.info.ink),
            { props: { variant: "outlined", color: "default" }, style: { borderColor: t.border.strong, color: t.text.primary } },
          ],
        },
      },
    },
    // Tab Item 10651:4922: 40 px, a 1 px grey line under the row, 2 px brand under the
    // active tab. The line is an inset shadow, so the indicator covers it (Figma draws no
    // grey under the active tab).
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40, boxShadow: `inset 0 -1px 0 ${t.border.strong}` },
        indicator: { height: 2, backgroundColor: t.brand.border },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          minWidth: 0,
          padding: "0 14px",
          borderRadius: "8px 8px 0 0",
          textTransform: "none",
          fontSize: 14,
          lineHeight: "22px",
          fontWeight: 500,
          color: t.text.secondary,
          "&:hover": { backgroundColor: t.hover, boxShadow: `inset 0 -1px 0 ${t.border.strong}` },
          "&.Mui-selected": { color: t.text.primary },
          "&.Mui-disabled": { color: t.text.disabled },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "12px",
          borderBottom: `1px solid ${t.border.subtle}`,
          fontSize: 14,
          lineHeight: "22px",
          color: t.text.primary,
          variants: [
            // Column Header 10659:68564: 44 px, sunken fill, caption type.
            {
              props: { variant: "head" },
              style: {
                height: 44,
                backgroundColor: t.surface.sunken,
                color: t.text.secondary,
                fontSize: 12,
                lineHeight: "16px",
                fontWeight: 500,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              },
            },
            // Column Cell 10659:68774: 64 px rows.
            { props: { variant: "body" }, style: { height: 64 } },
            { props: { padding: "checkbox" }, style: { padding: "0 0 0 4px" } },
            { props: { padding: "none" }, style: { padding: 0 } },
          ],
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { "&.MuiTableRow-hover:hover": { backgroundColor: t.hover } },
      },
    },
    // Admin · Action Menu 10708:8813.
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: "12px", border: `1px solid ${t.border.default}`, boxShadow: t.shadow.menu },
        list: { padding: "6px" },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 0,
          padding: "9px 10px",
          gap: "10px",
          borderRadius: "8px",
          fontSize: 14,
          lineHeight: "22px",
          color: t.text.primary,
          "&:hover": { backgroundColor: t.hover },
          "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: t.brand.subtle, color: t.brand.text },
          "& .MuiListItemIcon-root": { minWidth: 0 },
        },
      },
    },
    // Admin · Confirm Dialog 10708:8947.
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: "16px", border: `1px solid ${t.border.default}`, boxShadow: t.shadow.menu },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "18px 20px 16px",
          borderBottom: `1px solid ${t.border.default}`,
          fontSize: 18,
          lineHeight: "24px",
          fontWeight: 600,
          letterSpacing: "-0.2px",
          color: t.text.primary,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "18px 20px",
          ".MuiDialogTitle-root + &": { paddingTop: "18px" },
          // The title and the actions already draw the lines.
          variants: [{ props: { dividers: true }, style: { borderTop: "none", borderBottom: "none" } }],
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "14px 20px",
          gap: "8px",
          backgroundColor: t.surface.sunken,
          borderTop: `1px solid ${t.border.default}`,
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
        },
      },
    },
  },
});

export default theme;
